'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import clsx from 'clsx';

import {
  CTA,
  CourseModules,
  HowItWorks,
  PartnershipInfo,
  Plans,
  QEDUPlatform,
  VideoCard,
  WhoIsItFor,
  navigate,
} from '@widgets/Landing';
import { Button, CollapseCard, Popup } from '@shared/ui/components';
import ArrowIcon from '@public/icons/arrow-right.svg';
import LogoFull from '@public/logo-full.svg';
import BurgerIcon from '@public/icons/burger.svg';
import CloseIcon from '@public/icons/close.svg';

import styles from './HomePage.module.scss';

const navigationItems = [
  {
    label: 'ABOUT Q:EDU',
    href: [
      {
        label: <>Quantum Education</>,
        href: '#quantum-education',
      },
      { label: <>Vision</>, href: '#vision' },
      { label: <>Mission</>, href: '#mission' },
      {
        label: <>Quantum Technology Market</>,
        href: '#quantum-technology-market',
      },
    ],
  },
  { label: 'PARTNERSHIPS', href: '#partnerships' },
  {
    label: 'Program Plans / Pricing',
    href: [
      {
        label: (
          <>
            <div>Quantum Basic</div>
            <div>72,50 CHF/week</div>
          </>
        ),
        href: '#quantum-basic',
      },
      {
        label: (
          <>
            <div>Quantum Professional</div>
            <div>98,30 CHF/week</div>
          </>
        ),
        href: '#quantum-professional',
      },
      {
        label: (
          <>
            <div>Quantum Academia</div>
            <div>124,15 CHF/week</div>
          </>
        ),
        href: '#quantum-academia',
      },
      {
        label: (
          <>
            <div>Enterprise</div>
            <div>Price Available upon Consultation</div>
          </>
        ),
        href: '#enterprise',
      },
      {
        label: (
          <>
            <div>Teams or Multiple Users</div>
            <div>Price Available upon Consultation</div>
          </>
        ),
        href: '#teams',
      },
    ],
  },
  { label: 'HOW IT WORKS', href: '#how-it-works' },
  { label: 'WHO IS IT FOR', href: '#who-is-it-for' },
  { label: 'What You Learn', href: '#what-you-learn' },
  { label: 'Q:EDU ALUMNI', href: '#alumni' },
];

export default function HomePage() {
  const router = useRouter();
  const { isSignedIn } = useUser();

  const [navigationOpen, setNavigationOpen] = useState<string>();
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState<string>();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  return (
    <div id="landing" className={styles.container}>
      <header className={styles.header}>
        <div className={styles.header_left}>
          <div
            className={styles.header_burger}
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
          >
            <Image src={isMobileMenuOpen ? CloseIcon : BurgerIcon} alt="Menu" draggable={false} />
          </div>

          <Image src={LogoFull} className={styles.logo} alt="Logo" draggable={false} />
        </div>

        <nav className={styles.header_navigation}>
          {navigationItems.map((item) =>
            typeof item.href === 'string' ? (
              <div
                key={item.label}
                className={styles.navigation_item}
                onClick={() => navigate(item.href as string)}
              >
                {item.label}
              </div>
            ) : (
              <Popup
                key={item.label}
                isOpen={item.label === navigationOpen}
                anchor={
                  <div className={styles.navigation_item}>
                    <div>{item.label}</div>
                    <Image
                      src={ArrowIcon}
                      className={clsx(
                        styles.navigation_item_arrow,
                        item.label === navigationOpen && styles.navigation_item_arrow_open,
                      )}
                      alt="Arrow"
                      draggable={false}
                    />
                  </div>
                }
                positionY="down-out"
                positionX="left-in"
                popupClassName={styles.navigation_popup}
                preventScroll={false}
                setOpen={(o) => setNavigationOpen(o ? item.label : undefined)}
              >
                {item.href.map((href) => (
                  <div
                    key={href.href}
                    className={styles.navigation_popup_item}
                    onClick={() => {
                      navigate(href.href as string);
                      setNavigationOpen(undefined);
                    }}
                  >
                    {href.label}
                  </div>
                ))}
              </Popup>
            ),
          )}

          <div className={styles.navigation_item} onClick={() => router.push('/?contact-us=true')}>
            Contact
          </div>
        </nav>

        <nav
          className={clsx(
            styles.header_mobileNavigation,
            isMobileMenuOpen && styles.header_mobileNavigation_open,
          )}
        >
          <div>
            {navigationItems.map((item) =>
              typeof item.href === 'string' ? (
                <div
                  key={item.label}
                  className={styles.navigation_item}
                  onClick={() => {
                    navigate(item.href as string);
                    setMobileMenuOpen(false);
                  }}
                >
                  {item.label}
                </div>
              ) : (
                <CollapseCard
                  key={item.label}
                  isOpen={item.label === mobileNavigationOpen}
                  header={
                    <div className={styles.navigation_item}>
                      <div>{item.label}</div>

                      <Image
                        src={ArrowIcon}
                        className={clsx(
                          styles.navigation_item_arrow,
                          item.label === mobileNavigationOpen && styles.navigation_item_arrow_open,
                        )}
                        alt="Arrow"
                        draggable={false}
                      />
                    </div>
                  }
                  onClick={() =>
                    setMobileNavigationOpen(
                      item.label === mobileNavigationOpen ? undefined : item.label,
                    )
                  }
                >
                  {item.href.map((href) => (
                    <div
                      key={href.href}
                      className={styles.navigation_popup_item}
                      onClick={() => {
                        navigate(href.href as string);
                        setMobileNavigationOpen(undefined);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {href.label}
                    </div>
                  ))}
                </CollapseCard>
              ),
            )}

            <div
              className={styles.navigation_item}
              onClick={() => router.push('/?contact-us=true')}
            >
              Contact
            </div>
          </div>
        </nav>

        <div className={styles.header_actions}>
          <Link href="/modules">
            <Button size="l" variant="filled" className={styles.button}>
              {isSignedIn ? <div>Platform</div> : <div>Log in</div>}
              <Image src={ArrowIcon} alt="Arrow" draggable={false} />
            </Button>
          </Link>
        </div>
      </header>

      <main className={styles.main}>
        <QEDUPlatform />
        <PartnershipInfo />
        <Plans />
        <VideoCard containerClassName={styles.video} />
        <HowItWorks />
        <WhoIsItFor />
        <CourseModules />
        <CTA />
      </main>

      <footer className={styles.footer}>
        <div className={styles.footer_left}>Copyright © 2025 Q:EDU. All rights reserved.</div>

        <div className={styles.footer_right}>
          <Link href="/terms-and-conditions" className={styles.link}>
            Terms and Conditions
          </Link>

          <Link href="/privacy-policy" className={styles.link}>
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
}
